
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function RestaurantElectricalSystemsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://electriciansolutionsfresno.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Restaurant Electrical Systems',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional restaurant electrical installation services in Fresno CA for commercial kitchens, dining areas, and food service electrical systems',
    url: `${siteUrl}/services/commercial-specialist/restaurant-electrical-systems`,
  };

  const features = [
    {
      icon: 'ri-restaurant-line',
      title: 'Commercial Kitchen Electrical',
      description: 'Heavy-duty circuits for ovens, ranges, fryers, and kitchen equipment',
    },
    {
      icon: 'ri-fire-line',
      title: 'Hood System Electrical',
      description: 'Electrical connections for exhaust hoods and fire suppression systems',
    },
    {
      icon: 'ri-fridge-line',
      title: 'Refrigeration Circuits',
      description: 'Dedicated power for walk-in coolers, freezers, and refrigeration units',
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Dining Area Lighting',
      description: 'Ambient lighting design with dimming controls for dining atmosphere',
    },
    {
      icon: 'ri-bank-card-line',
      title: 'POS System Electrical',
      description: 'Reliable circuits for point-of-sale terminals and payment systems',
    },
    {
      icon: 'ri-plug-line',
      title: 'Equipment Circuits',
      description: 'Proper electrical for dishwashers, ice machines, and food prep equipment',
    },
  ];

  return (
    <>
      <SEOHead
        title="Restaurant Electrical Systems Fresno | Commercial Kitchen Electrician CA"
        description="Expert restaurant electrical services in Fresno CA. Commercial kitchen wiring, hood systems, refrigeration circuits, dining lighting, and food service electrical. Licensed contractors."
        canonical="/services/commercial-specialist/restaurant-electrical-systems"
        keywords="restaurant electrician Fresno, commercial kitchen electrical, food service electrical, restaurant wiring"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Restaurant Electrical Systems — Fresno, CA"
        heroSubtitle="Specialized electrical services for restaurants and food service businesses in Fresno"
        heroImage="https://public.readdy.ai/ai/img_res/31e27b7ea27aad528beb54f6815359e0.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Heavy%20duty%20electrical%20panel%20with%20large%20circuit%20breakers%20and%20organized%20wiring%20for%20commercial%20kitchen%20equipment%20in%20restaurant%20setting%20with%20conduit%20runs%20and%20professional%20installation&width=700&height=600&seq=restaurant-elec-fresno-001&orientation=portrait"
        fresnoContent="Serving Fresno's restaurant and food service industry with expert electrical solutions. From Tower District eateries to downtown restaurants, fast-casual chains to fine dining establishments throughout Fresno, we provide specialized electrical installations for commercial kitchens and dining spaces. Our licensed electricians understand the demanding electrical requirements of food service operations—heavy-duty equipment circuits, health code compliance, and reliable power for uninterrupted service. We deliver code-compliant electrical systems that keep your Fresno restaurant running safely and efficiently."
        features={features}
        faqs={[
          {
            question: 'What electrical services do restaurants need in Fresno?',
            answer:
              'Restaurants in Fresno CA require specialized electrical services including heavy-duty circuits for commercial kitchen equipment (ovens, ranges, fryers, griddles), electrical connections for exhaust hood systems and fire suppression, dedicated circuits for walk-in coolers and freezers, three-phase power for large equipment, dishwasher and ice machine circuits, POS system and payment terminal wiring, dining area lighting with dimming controls, exterior signage electrical, emergency lighting and exit signs, and adequate electrical capacity for all equipment. We design restaurant electrical systems that meet health department requirements and California electrical codes.',
          },
          {
            question: 'How much does restaurant electrical installation cost in Fresno?',
            answer:
              'Restaurant electrical costs in Fresno vary widely based on size and equipment. Small cafes or quick-service restaurants typically range from $15,000-$40,000 for complete electrical installation. Full-service restaurants average $40,000-$100,000 for kitchen and dining area electrical. Large restaurants or those with extensive equipment may require $100,000-$250,000+ for complete electrical systems. Commercial kitchen electrical alone typically costs $20,000-$75,000 depending on equipment load. We provide detailed estimates after reviewing your equipment list and restaurant layout. Proper electrical planning prevents costly changes during construction.',
          },
          {
            question: 'Can you handle three-phase power for restaurant equipment in Fresno?',
            answer:
              'Yes, we specialize in three-phase power installation for Fresno restaurants. Many commercial kitchen equipment items require three-phase power including large ovens and ranges, commercial dishwashers, heavy-duty HVAC systems, and high-capacity refrigeration. We coordinate with PG&E to bring three-phase service to your restaurant location, install appropriate panels and disconnects, provide proper circuits for each piece of equipment, ensure correct phase balancing, and meet all California electrical codes. Our licensed electricians are experienced with three-phase restaurant electrical systems throughout Fresno.',
          },
          {
            question: 'Do you work with health department requirements in Fresno?',
            answer:
              'Absolutely. We are familiar with Fresno County health department electrical requirements for food service establishments. Restaurant electrical must meet specific codes including proper GFCI protection in food prep areas, adequate lighting levels for food safety, electrical for hand washing stations, proper circuits for refrigeration equipment, emergency lighting for safe egress, and electrical for exhaust hood systems. We ensure all electrical work meets health department standards and passes inspections. We coordinate with health inspectors and provide documentation required for restaurant permits in Fresno.',
          },
          {
            question: 'Can you work during restaurant closure periods in Fresno?',
            answer:
              'Yes, we specialize in restaurant electrical work during closure periods throughout Fresno. Most restaurant electrical installations and major upgrades are completed during planned closure periods, overnight hours, or early mornings before opening. For restaurant remodels, we work efficiently during temporary closures to minimize lost revenue. Emergency electrical repairs receive priority response to get your restaurant operational quickly. We understand the financial impact of restaurant downtime and work diligently to complete electrical projects on schedule so you can serve Fresno customers without extended interruption.',
          },
          {
            question: 'Are you licensed for commercial restaurant electrical work in Fresno?',
            answer:
              "Yes, we hold a California C-10 Electrical Contractor license required for all commercial restaurant electrical work in Fresno CA. Our electricians are experienced in commercial kitchen electrical systems, three-phase power, heavy-duty equipment circuits, and health code compliance. We carry full commercial liability insurance and workers' compensation coverage. All work is permitted through the City of Fresno Building Department and inspected for code compliance. We work closely with restaurant owners, general contractors, and equipment suppliers to deliver complete electrical solutions for Fresno food service establishments.",
          },
        ]}
      />
    </>
  );
}
